import React, { useEffect, useState } from "react";
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from "reactstrap";
import { useForm, useFieldArray } from "react-hook-form";
import { callApi } from "../../../ApiHelper/ApiHelper";
import { apiUrl } from "../../../ApiHelper/ApiUrl";
import { toast } from "react-toastify";
import moment from "moment";
import { safeText } from "../utils/hubHelpers";

export default function CreateStockOutModal({ isOpen, toggle, defaultSiteId, onSuccess }) {
  const { register, handleSubmit, reset, control, setValue, watch, formState: { errors } } = useForm({
    defaultValues: {
      siteId: defaultSiteId || "",
      date: moment().format("YYYY-MM-DD"),
      type: "Site Consumption",
      item: [{ itemId: "", quantity: "", remarks: "" }],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control,
    name: "item",
  });

  const [loading, setLoading] = useState(false);
  const [sites, setSites] = useState([]);
  const [availableItems, setAvailableItems] = useState([]);
  const [machines, setMachines] = useState([]);

  const selectedSiteId = watch("siteId");

  useEffect(() => {
    if (isOpen) {
      fetchSitesAndMachines();
      fetchItems();
      if (defaultSiteId) {
        setValue("siteId", defaultSiteId);
      }
    }
  }, [isOpen, defaultSiteId]);

  const fetchSitesAndMachines = async () => {
    try {
      const [siteRes, machRes] = await Promise.all([
        callApi(apiUrl.get_site, { method: "GET" }),
        callApi(`${apiUrl.get_machinery}?pageSize=200`, { method: "GET" }),
      ]);

      if (siteRes?.status === true) {
        const list = Array.isArray(siteRes?.data?.site)
          ? siteRes.data.site
          : Array.isArray(siteRes?.data)
          ? siteRes.data
          : [];
        setSites(list);
      }

      if (machRes?.status === true) {
        const list = Array.isArray(machRes?.data?.machine)
          ? machRes.data.machine
          : Array.isArray(machRes?.data)
          ? machRes.data
          : [];
        setMachines(list);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const fetchItems = async () => {
    try {
      const res = await callApi(`${apiUrl.get_items}pageSize=300`, { method: "GET" });
      if (res?.status === true) {
        const list = Array.isArray(res?.data?.items)
          ? res.data.items
          : Array.isArray(res?.data)
          ? res.data
          : [];
        setAvailableItems(list);
      }
    } catch (e) {
      console.error(e);
    }
  };

  const getFinancialYear = () => {
    const today = moment();
    const curYear = today.month() < 3 ? today.year() - 1 : today.year();
    return `${curYear}-${curYear + 1}`;
  };

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const payload = {
        siteId: data.siteId,
        date: data.date,
        type: data.type || "Site Consumption",
        financialYear: getFinancialYear(),
        item: data.item.map((it) => ({
          itemId: it.itemId,
          quantity: parseFloat(it.quantity) || 1,
          remarks: it.remarks || "",
        })),
      };

      const res = await callApi(apiUrl.create_stock_out, {
        method: "POST",
        data: payload,
      });

      if (res?.status === true) {
        toast.success("Stock Out recorded successfully!");
        reset();
        toggle();
        onSuccess?.();
      } else {
        toast.error(res?.message || "Failed to record stock out");
      }
    } catch (err) {
      toast.error(err?.message || "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal isOpen={isOpen} toggle={toggle} size="lg" centered>
      <form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader
          toggle={toggle}
          close={
            <button
              type="button"
              className="btn-close btn-close-white"
              onClick={toggle}
              aria-label="Close"
              style={{ filter: "invert(1) grayscale(100%) brightness(200%)", opacity: 0.9, cursor: "pointer" }}
            />
          }
          style={{ background: "#0f172a", color: "#fff", display: "flex", alignItems: "center", justifyContent: "space-between" }}
        >
          📦 Issue Spare Parts & Stock Out
        </ModalHeader>
        <ModalBody>
          <div className="row g-3">
            <div className="col-md-6">
              <label className="form-label">
                Site Location *
              </label>
              <select
                className="form-select"
                {...register("siteId", { required: "Site is required" })}
              >
                <option value="">Select Project / Solar Site</option>
                {sites.map((s) => (
                  <option key={s.id} value={s.id}>
                    {safeText(s.name, "Site")}
                  </option>
                ))}
              </select>
              {errors.siteId && <small className="text-danger">{errors.siteId.message}</small>}
            </div>

            <div className="col-md-3">
              <label className="form-label">
                Issue Date *
              </label>
              <input
                type="date"
                className="form-control"
                {...register("date", { required: "Date is required" })}
              />
            </div>

            <div className="col-md-3">
              <label className="form-label">
                Issue Purpose
              </label>
              <select className="form-select" {...register("type")}>
                <option value="Site Consumption">Site Consumption</option>
                <option value="Machinery Repair">Machinery Repair</option>
                <option value="Routine Service">Routine Service</option>
                <option value="Direct Issue">Direct Issue</option>
              </select>
            </div>

            <div className="col-12">
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid #e2e8f0", paddingBottom: "4px", marginTop: "4px" }}>
                <h6 style={{ fontWeight: 700, color: "#0f172a", margin: 0 }}>
                  Items / Spare Parts to Dispatch
                </h6>
                <button
                  type="button"
                  onClick={() => append({ itemId: "", quantity: "", remarks: "" })}
                  style={{
                    background: "#eff6ff",
                    color: "#2563eb",
                    border: "1px solid #bfdbfe",
                    borderRadius: 6,
                    padding: "4px 10px",
                    fontSize: "12.5px",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  + Add Item
                </button>
              </div>
            </div>

            {fields.map((field, idx) => (
              <div key={field.id} className="col-12" style={{ background: "#f8fafc", padding: "0.75rem", borderRadius: 6, border: "1px solid #e2e8f0" }}>
                <div className="row g-2 align-items-center">
                  <div className="col-md-5">
                    <label className="form-label">Item / Spare Part *</label>
                    <select
                      className="form-select"
                      {...register(`item.${idx}.itemId`, { required: "Select item" })}
                    >
                      <option value="">Select Item</option>
                      {availableItems.map((it) => (
                        <option key={it.id} value={it.id}>
                          {safeText(it.name, "Item")} {it.unit ? `(${it.unit})` : ""} {it.hsnCode ? `[HSN: ${it.hsnCode}]` : ""}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Quantity *</label>
                    <input
                      type="number"
                      step="any"
                      className="form-control"
                      placeholder="e.g. 5"
                      {...register(`item.${idx}.quantity`, { required: "Qty required" })}
                    />
                  </div>

                  <div className="col-md-3">
                    <label className="form-label">Machine / Note</label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="e.g. JCB Hydraulic Filter"
                      {...register(`item.${idx}.remarks`)}
                    />
                  </div>

                  <div className="col-md-1 text-end" style={{ paddingTop: 20 }}>
                    {fields.length > 1 && (
                      <button
                        type="button"
                        onClick={() => remove(idx)}
                        style={{
                          background: "#fee2e2",
                          color: "#dc2626",
                          border: "none",
                          borderRadius: 6,
                          width: 32,
                          height: 32,
                          cursor: "pointer",
                          fontWeight: 700,
                          fontSize: "14px",
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={toggle} disabled={loading}>
            Cancel
          </Button>
          <Button color="primary" type="submit" disabled={loading} style={{ background: "#2563eb", borderColor: "#2563eb" }}>
            {loading ? "Recording..." : "Issue & Stock Out"}
          </Button>
        </ModalFooter>
      </form>
    </Modal>
  );
}
